import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import toast from 'react-hot-toast';

/**
 * AddAccountModal Component
 * 
 * Modal for creating new chart of accounts entries with comprehensive validation
 * and Saudi GAAP compliance features
 */
const AddAccountModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    accountCode: '',
    accountName: '',
    accountNameAr: '',
    accountType: 'asset',
    category: '',
    description: '',
    openingBalance: 0,
    normalBalance: 'debit',
    isActive: true,
    allowManualEntries: true,
    requireDepartment: false,
    isTaxRelated: false,
    isVatAccount: false,
    isZakatAccount: false
  });

  // Account type configurations
  const accountTypes = [
    { value: 'asset', label: t('coa.account_types.asset', 'Asset'), normalBalance: 'debit' },
    { value: 'liability', label: t('coa.account_types.liability', 'Liability'), normalBalance: 'credit' },
    { value: 'equity', label: t('coa.account_types.equity', 'Equity'), normalBalance: 'credit' },
    { value: 'revenue', label: t('coa.account_types.revenue', 'Revenue'), normalBalance: 'credit' },
    { value: 'expense', label: t('coa.account_types.expense', 'Expense'), normalBalance: 'debit' }
  ];

  const categories = {
    asset: [
      { value: 'current_asset', label: t('coa.categories.current_asset', 'Current Asset') },
      { value: 'fixed_asset', label: t('coa.categories.fixed_asset', 'Fixed Asset') },
      { value: 'intangible_asset', label: t('coa.categories.intangible_asset', 'Intangible Asset') }
    ],
    liability: [
      { value: 'current_liability', label: t('coa.categories.current_liability', 'Current Liability') },
      { value: 'long_term_liability', label: t('coa.categories.long_term_liability', 'Long-term Liability') }
    ],
    equity: [
      { value: 'equity', label: t('coa.categories.equity', 'Equity') }
    ],
    revenue: [
      { value: 'operating_revenue', label: t('coa.categories.operating_revenue', 'Operating Revenue') },
      { value: 'other_revenue', label: t('coa.categories.other_revenue', 'Other Revenue') }
    ],
    expense: [
      { value: 'operating_expense', label: t('coa.categories.operating_expense', 'Operating Expense') },
      { value: 'administrative_expense', label: t('coa.categories.administrative_expense', 'Administrative Expense') },
      { value: 'other_expense', label: t('coa.categories.other_expense', 'Other Expense') }
    ]
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        accountCode: '',
        accountName: '',
        accountNameAr: '',
        accountType: 'asset',
        category: 'current_asset',
        description: '',
        openingBalance: 0,
        normalBalance: 'debit',
        isActive: true,
        allowManualEntries: true,
        requireDepartment: false,
        isTaxRelated: false,
        isVatAccount: false,
        isZakatAccount: false
      });
      setErrors({});
    }
  }, [isOpen]);

  // Update normal balance when account type changes
  useEffect(() => {
    const accountType = accountTypes.find(type => type.value === formData.accountType);
    if (accountType) {
      setFormData(prev => ({
        ...prev,
        normalBalance: accountType.normalBalance,
        category: categories[formData.accountType]?.[0]?.value || ''
      }));
    }
  }, [formData.accountType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Account Code validation
    if (!formData.accountCode) {
      newErrors.accountCode = t('coa.validation.code_required', 'Account code is required');
    } else if (!/^\d{4}$/.test(formData.accountCode)) {
      newErrors.accountCode = t('coa.validation.code_format', 'Account code must be exactly 4 digits');
    }
    
    // Account Name validation
    if (!formData.accountName.trim()) {
      newErrors.accountName = t('coa.validation.name_required', 'Account name is required');
    }
    
    // Category validation
    if (!formData.category) {
      newErrors.category = t('coa.validation.category_required', 'Category is required');
    }
    
    // Opening balance validation
    if (isNaN(formData.openingBalance)) {
      newErrors.openingBalance = t('coa.validation.balance_numeric', 'Opening balance must be a number');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      await chartOfAccountsService.addAccount({
        ...formData,
        companyId: currentCompany.id,
        createdBy: currentUser?.uid || 'system',
        openingBalance: parseFloat(formData.openingBalance) || 0
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error creating account:', error);
      
      if (error.message.includes('already exists')) {
        setErrors({ accountCode: t('coa.validation.code_exists', 'Account code already exists') });
      } else if (error.message.includes('4 digits')) {
        setErrors({ accountCode: t('coa.validation.code_format', 'Account code must be exactly 4 digits') });
      } else {
        toast.error(t('coa.error.create_failed', 'Failed to create account'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <PlusIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('coa.add_account', 'Add Account')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('coa.add_account_description', 'Create a new chart of accounts entry')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Type Selection - Make this first and prominent */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-blue-900 mb-3">
                {t('coa.step_1', 'Step 1: Choose Account Type')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {accountTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('accountType', type.value)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.accountType === type.value
                        ? 'border-blue-500 bg-blue-100 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg mb-1">
                        {type.value === 'asset' && '💰'}
                        {type.value === 'liability' && '💳'}
                        {type.value === 'equity' && '🏛️'}
                        {type.value === 'revenue' && '💹'}
                        {type.value === 'expense' && '💸'}
                      </div>
                      <div className="text-xs font-medium">{type.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                {t('coa.step_2', 'Step 2: Account Details')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('coa.account_code', 'Account Code')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.accountCode}
                    onChange={(e) => handleInputChange('accountCode', e.target.value)}
                    className={`input input-bordered w-full ${errors.accountCode ? 'border-red-500' : ''}`}
                    placeholder={`${formData.accountType === 'asset' ? '1000' : formData.accountType === 'liability' ? '2000' : formData.accountType === 'equity' ? '3000' : formData.accountType === 'revenue' ? '4000' : '5000'} (4 ${t('coa.digits', 'digits')})`}
                    maxLength={4}
                  />
                  {errors.accountCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.accountCode}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.accountType === 'asset' && t('coa.code_hint_asset', 'Assets: 1000-1999')}
                    {formData.accountType === 'liability' && t('coa.code_hint_liability', 'Liabilities: 2000-2999')}
                    {formData.accountType === 'equity' && t('coa.code_hint_equity', 'Equity: 3000-3999')}
                    {formData.accountType === 'revenue' && t('coa.code_hint_revenue', 'Revenue: 4000-4999')}
                    {formData.accountType === 'expense' && t('coa.code_hint_expense', 'Expenses: 5000-5999')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('coa.category', 'Category')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={`select select-bordered w-full ${errors.category ? 'border-red-500' : ''}`}
                  >
                    <option value="">{t('coa.select_category', 'Select Category')}</option>
                    {categories[formData.accountType]?.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Names */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.account_name', 'Account Name (English)')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) => handleInputChange('accountName', e.target.value)}
                  className={`input input-bordered w-full ${errors.accountName ? 'border-red-500' : ''}`}
                  placeholder={t('coa.account_name_placeholder', 'e.g., Cash on Hand, Accounts Receivable')}
                />
                {errors.accountName && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('coa.account_name_ar', 'Account Name (Arabic)')}
                </label>
                <input
                  type="text"
                  value={formData.accountNameAr}
                  onChange={(e) => handleInputChange('accountNameAr', e.target.value)}
                  className="input input-bordered w-full"
                  placeholder={t('coa.account_name_ar_placeholder', 'مثال: النقدية في الصندوق، حسابات المدينين')}
                  dir="rtl"
                />
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-green-900 mb-3">
                {t('coa.step_3', 'Step 3: Financial Details')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('coa.opening_balance', 'Opening Balance')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.openingBalance}
                      onChange={(e) => handleInputChange('openingBalance', e.target.value)}
                      className={`input input-bordered w-full pr-12 ${errors.openingBalance ? 'border-red-500' : ''}`}
                      placeholder="0.00"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                      SAR
                    </div>
                  </div>
                  {errors.openingBalance && (
                    <p className="text-red-500 text-sm mt-1">{errors.openingBalance}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {t('coa.opening_balance_hint', 'Enter the starting balance for this account')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('coa.normal_balance', 'Normal Balance')}
                  </label>
                  <select
                    value={formData.normalBalance}
                    onChange={(e) => handleInputChange('normalBalance', e.target.value)}
                    className="select select-bordered w-full"
                    disabled
                  >
                    <option value="debit">{t('coa.debit', 'Debit')} (Dr)</option>
                    <option value="credit">{t('coa.credit', 'Credit')} (Cr)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('coa.normal_balance_hint', 'Automatically set based on account type')}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('coa.description', t('common.description'))}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="3"
                className="textarea textarea-bordered w-full"
                placeholder={t('coa.description_placeholder', 'Optional: Describe what this account is used for...')}
              />
            </div>

            {/* Account Settings */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-purple-900 mb-3">
                {t('coa.step_4', 'Step 4: Account Settings')}
              </h3>
              
              <div className="space-y-3">
                {/* Basic Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="form-checkbox h-4 w-4 text-blue-600 mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        {t('coa.is_active', 'Active Account')}
                      </span>
                      <p className="text-xs text-gray-500">
                        {t('coa.is_active_hint', 'Account can be used in transactions')}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.allowManualEntries}
                      onChange={(e) => handleInputChange('allowManualEntries', e.target.checked)}
                      className="form-checkbox h-4 w-4 text-blue-600 mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        {t('coa.allow_manual_entries', 'Allow Manual Entries')}
                      </span>
                      <p className="text-xs text-gray-500">
                        {t('coa.allow_manual_hint', 'Users can post journal entries to this account')}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Saudi-Specific Settings */}
                <div className="border-t border-purple-200 pt-3">
                  <h4 className="text-xs font-medium text-purple-800 mb-2">
                    {t('coa.saudi_compliance', 'Saudi Compliance Settings')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-start space-x-2 p-2 bg-white rounded border border-gray-200">
                      <input
                        type="checkbox"
                        checked={formData.isVatAccount}
                        onChange={(e) => handleInputChange('isVatAccount', e.target.checked)}
                        className="form-checkbox h-3 w-3 text-blue-600 mt-1"
                      />
                      <div>
                        <span className="text-xs font-medium text-gray-700">
                          {t('coa.is_vat_account', 'VAT Account')}
                        </span>
                        <p className="text-xs text-gray-500">
                          15% VAT
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start space-x-2 p-2 bg-white rounded border border-gray-200">
                      <input
                        type="checkbox"
                        checked={formData.isZakatAccount}
                        onChange={(e) => handleInputChange('isZakatAccount', e.target.checked)}
                        className="form-checkbox h-3 w-3 text-blue-600 mt-1"
                      />
                      <div>
                        <span className="text-xs font-medium text-gray-700">
                          {t('coa.is_zakat_account', 'Zakat Account')}
                        </span>
                        <p className="text-xs text-gray-500">
                          2.5% Zakat
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start space-x-2 p-2 bg-white rounded border border-gray-200">
                      <input
                        type="checkbox"
                        checked={formData.isTaxRelated}
                        onChange={(e) => handleInputChange('isTaxRelated', e.target.checked)}
                        className="form-checkbox h-3 w-3 text-blue-600 mt-1"
                      />
                      <div>
                        <span className="text-xs font-medium text-gray-700">
                          {t('coa.is_tax_related', 'Tax Related')}
                        </span>
                        <p className="text-xs text-gray-500">
                          GAZT reporting
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                {t('common.creating', 'Creating...')}
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4 mr-2" />
                {t('coa.create_account', 'Create Account')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAccountModal;